// Story 8.2 G3 — the earnings → balance confirm modal.
//
// The UX-DR24 confirm-modal pattern is not decoration here: the move is
// IRREVERSIBLE. Earned money is payable (Story 8.5 builds that rail);
// Greenhold balance is not. Moving it is a choice to spend it here instead of
// being paid it, and there is no way back — so it is confirmed, and the modal
// says so in those words.
//
// Copy discipline (UX-DR49): "earnings", "revenue share" and "paid out" belong
// to the earned pot. The wallet pot is "your Greenhold balance" — never "your
// money", never "funds", never "cash".

import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@greenhold/components";
import toast from "react-hot-toast";

import {
  useConvertEarnings,
  type ConvertEarningsError,
} from "./useConvertEarnings";

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

function parseEurToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const num = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num * 100);
}

type ConvertEarningsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The EARNINGS pot, in cents. The wallet pot is irrelevant here: it is
   *  never a source, only a destination. */
  earningsCents: number;
};

export function ConvertEarningsModal({
  open,
  onOpenChange,
  earningsCents,
}: ConvertEarningsModalProps) {
  const [amountInput, setAmountInput] = useState<string>("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const convert = useConvertEarnings();
  // Depend on the stable `reset` fn, not the whole mutation object: a new
  // object identity every render + reset() notifying the observer =
  // "Maximum update depth". (Cited CashoutModal until Story 8.3 deleted it.)
  const { reset: resetConvert } = convert;

  useEffect(() => {
    if (!open) {
      setAmountInput("");
      setInlineError(null);
      resetConvert();
    }
  }, [open, resetConvert]);

  useEffect(() => {
    if (open) {
      setAmountInput((earningsCents / 100).toFixed(2));
    }
  }, [open, earningsCents]);

  const amountCents = parseEurToCents(amountInput);
  const validationError = ((): string | null => {
    if (amountCents == null) return null;
    if (amountCents > earningsCents)
      return `You have ${formatEur(earningsCents)} in earnings.`;
    return null;
  })();

  const canSubmit =
    amountCents != null && validationError == null && !convert.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || amountCents == null) return;
    setInlineError(null);
    try {
      await convert.mutateAsync({
        amountCents,
        idempotencyKey: crypto.randomUUID(),
      });
      toast.success(
        `${formatEur(amountCents)} moved to your Greenhold balance.`,
      );
      onOpenChange(false);
    } catch (err) {
      const detail = err as ConvertEarningsError;
      setInlineError(
        detail.message ?? "Could not move your earnings. Try again.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move earnings to your balance</DialogTitle>
          <DialogDescription>
            Your Greenhold balance is for spending on Greenhold. Earnings you
            move here stay here — this cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm">
            <span className="text-muted-foreground">Creator earnings: </span>
            <span className="font-semibold tabular-nums">
              {formatEur(earningsCents)}
            </span>
          </div>

          <div className="space-y-1">
            <Label htmlFor="convert-amount">Amount (EUR)</Label>
            <Input
              id="convert-amount"
              type="text"
              inputMode="decimal"
              autoFocus
              value={amountInput}
              onChange={(e) => {
                setAmountInput(e.target.value);
                setInlineError(null);
              }}
              placeholder="0.00"
            />
            <p className="text-xs text-muted-foreground">
              {validationError ?? "No fee — every cent arrives."}
            </p>
          </div>

          {inlineError && (
            <p className="text-sm text-destructive" role="alert">
              {inlineError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="tertiary"
              onClick={() => onOpenChange(false)}
              disabled={convert.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {convert.isPending ? "Moving…" : "Move to balance"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
